import type { ContentTreeResponse, EditSessionThread } from '$lib/api/types.gen';

type ThreadState = Pick<EditSessionThread, 'id' | 'archive_ts'> & { deleteTs?: number };

export function isArchivedThreadContentError(error: unknown): boolean {
	return (
		!!error &&
		typeof error === 'object' &&
		'kind' in error &&
		error.kind === 'CONFLICT' &&
		'message' in error &&
		error.message === 'edit_session.thread content is unavailable while archived'
	);
}

/** Keep the tree empty while the tab lifecycle navigates out of an unavailable thread. */
function unavailableTree(thread: ThreadState): ContentTreeResponse {
	return {
		requested_node_id: thread.id,
		nodes: [],
		metadata: { index: {}, depth_reached: 0 },
	};
}

export async function loadCurrentThreadContentTree(source: {
	loadThread(force: boolean): Promise<ThreadState>;
	loadContentTree(): Promise<ContentTreeResponse>;
}): Promise<ContentTreeResponse> {
	const thread = await source.loadThread(false);
	if (thread.archive_ts !== undefined || thread.deleteTs !== undefined)
		return unavailableTree(thread);
	try {
		return await source.loadContentTree();
	} catch (error) {
		if (!isArchivedThreadContentError(error)) throw error;
		// Archival can commit after the metadata check or before its SSE event arrives.
		// Refreshing metadata also notifies the tab strip so it closes the tab.
		const refreshed = await source.loadThread(true);
		if (refreshed.archive_ts === undefined) throw error;
		return unavailableTree(refreshed);
	}
}
